import { useEffect, useState, useCallback, type FormEvent } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { CheckCircle2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { getAppUrl, isCrossDomainConfigured, isOnAppDomain } from "@/lib/auth-redirect";
import { rememberUpgradeIntent, upgradePlanFromUrl } from "@/lib/upgrade-intent";
import { consumeStoredAuthError } from "@/lib/auth-messages";
import { apiLogin, apiSignup, setSession, clearSession } from "@/lib/auth-session";
import { apiUrl } from "@/lib/api-base";
import { requestPasswordReset } from "@/lib/auth-api";
import { AUTH_STYLES, AuthBackdrop, AuthShell } from "@/components/auth-shell";

const RESEND_COOLDOWN_S = 30;

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}

export default function LoginPage() {
  const { isAuthenticated, isLoading: authLoading, hydrateUser } = useAuth();
  const [, setLocation] = useLocation();
  const search = useSearch();
  const [mode, setMode] = useState<"signin" | "signup" | "forgot">(
    new URLSearchParams(search).get("mode") === "forgot" ? "forgot" : "signin",
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [forgotOutcome, setForgotOutcome] = useState<"sent" | "not_found" | "no_password" | null>(null);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Always render the login page on the app domain so the resulting session
  // lands in the right localStorage. If we somehow ended up on the marketing
  // origin (apex) in split-domain mode, hop immediately.
  useEffect(() => {
    if (isCrossDomainConfigured() && !isOnAppDomain()) {
      // Keep the query (e.g. `?upgrade=plus`) across the hop to the app origin.
      window.location.replace(`${getAppUrl()}/login${window.location.search}`);
      return;
    }
    // Arrived via "Upgrade" while signed out: remember the plan so checkout can resume
    // after sign-in (see UpgradeIntentResume in App.tsx).
    const upgradePlan = upgradePlanFromUrl();
    if (upgradePlan) rememberUpgradeIntent(upgradePlan);
  }, []);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      if (isCrossDomainConfigured() && !isOnAppDomain()) {
        window.location.replace(`${getAppUrl()}/`);
        return;
      }
      setLocation("/");
    }
  }, [authLoading, isAuthenticated, setLocation]);

  useEffect(() => {
    clearSession();
  }, []);

  // Surfaces a message queued by another part of the app (e.g. a 401 session-expiry
  // redirect) plus outcomes the backend communicates via query params on its own
  // redirects (email verification, Google OAuth failure) — then cleans the URL.
  useEffect(() => {
    const stored = consumeStoredAuthError();
    if (stored) {
      setMessage({ type: "err", text: stored });
      return;
    }
    const params = new URLSearchParams(search);
    if (params.get("verified") === "1") {
      setMessage({ type: "ok", text: "Your email is confirmed — you can sign in now." });
    } else if (params.get("verify_error") === "1") {
      setMessage({ type: "err", text: "That confirmation link is invalid or has expired. Please sign up again." });
    } else if (params.get("error") === "google_auth_failed") {
      setMessage({ type: "err", text: "Google sign-in didn't go through. Please try again or use your email and password." });
    } else if (params.get("error") === "google_unavailable") {
      setMessage({ type: "err", text: "Google sign-in isn't available right now. Please use your email and password instead." });
    } else {
      return;
    }
    window.history.replaceState(null, document.title, window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signInWithGoogle = useCallback(() => {
    window.location.href = apiUrl("/auth/google");
  }, []);

  const redirectAfterAuth = useCallback(() => {
    // In split-domain prod (app.* vs apex), email/password flows on the
    // marketing domain must hop to the app origin so the just-stored session
    // is on the right localStorage. Same-origin / dev keeps wouter nav.
    if (isCrossDomainConfigured() && !isOnAppDomain()) {
      window.location.replace(`${getAppUrl()}/`);
      return;
    }
    setLocation("/");
  }, [setLocation]);

  const onSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setMessage(null);
      setSubmitting(true);
      try {
        if (mode === "forgot") {
          const result = await requestPasswordReset(email.trim());
          setForgotOutcome(result.status === "error" ? null : result.status);

          if (result.status === "error") {
            setMessage({ type: "err", text: result.error });
            return;
          }
          if (result.status === "not_found") {
            setMessage({ type: "err", text: "No account found with that email." });
            return;
          }
          if (result.status === "no_password") {
            setMessage({
              type: "ok",
              text: "This account signs in with Google. We've also sent a link to your email (and spam folder) if you'd like to set a password too.",
            });
            setResendCooldown(RESEND_COOLDOWN_S);
            return;
          }
          setMessage({
            type: "ok",
            text: "Check your inbox (and spam folder) for a link to reset your password.",
          });
          setResendCooldown(RESEND_COOLDOWN_S);
          return;
        }

        if (mode === "signup") {
          const result = await apiSignup(email.trim(), password, firstName.trim(), lastName.trim());
          if (!result.ok) {
            setMessage({ type: "err", text: result.error });
            return;
          }
          setMessage({
            type: "ok",
            text: "We've sent a confirmation link to your email. Please check your inbox and confirm your address, then come back here to sign in.",
          });
          setMode("signin");
        } else {
          const result = await apiLogin(email.trim(), password);
          if (!result.ok) {
            setMessage({ type: "err", text: result.error });
            return;
          }
          setSession({ accessToken: result.data.accessToken, refreshToken: result.data.refreshToken });
          hydrateUser(result.data.user);
          redirectAfterAuth();
        }
      } finally {
        setSubmitting(false);
      }
    },
    [email, password, firstName, lastName, mode, redirectAfterAuth, hydrateUser],
  );

  if (authLoading || isAuthenticated) {
    return (
      <div className="relative isolate flex min-h-screen items-center justify-center bg-night">
        <AuthBackdrop />
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-lilac-light border-t-transparent" />
      </div>
    );
  }

  return (
    <AuthShell backHref="/" backLabel="← Back to home">
      <Card className={AUTH_STYLES.card}>
        <CardHeader className={AUTH_STYLES.header}>
          <img
            src="/images/UnfoldWin - dark.png"
            alt=""
            className={`${AUTH_STYLES.badge} object-cover`}
          />
          <CardTitle className={AUTH_STYLES.title}>
            {mode === "forgot" ? "Reset your password" : mode === "signin" ? "Welcome back" : "Create your account"}
          </CardTitle>
          <CardDescription className={AUTH_STYLES.subtitle}>
            {mode === "forgot"
              ? "Enter your email and we'll send you a link to choose a new password."
              : mode === "signin"
                ? "Sign in to continue to UnfoldWIN."
                : "Start unfolding your momentum."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {message && (
            <div
              role="alert"
              className={
                message.type === "err"
                  ? "text-sm rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2"
                  : "flex items-start gap-2 text-sm rounded-md border border-primary/20 bg-primary/5 text-foreground px-3 py-2"
              }
            >
              {message.type === "ok" && (
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
              )}
              <span>{message.text}</span>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            {mode === "signup" && (
              <div className="grid grid-cols-2 gap-3">
                <div className={AUTH_STYLES.field}>
                  <Label htmlFor="first-name">First name</Label>
                  <Input
                    id="first-name"
                    type="text"
                    autoComplete="given-name"
                    required
                    maxLength={80}
                    value={firstName}
                    onChange={(ev) => setFirstName(ev.target.value)}
                    placeholder="Maya"
                    className={AUTH_STYLES.input}
                  />
                </div>
                <div className={AUTH_STYLES.field}>
                  <Label htmlFor="last-name">Last name</Label>
                  <Input
                    id="last-name"
                    type="text"
                    autoComplete="family-name"
                    required
                    maxLength={80}
                    value={lastName}
                    onChange={(ev) => setLastName(ev.target.value)}
                    placeholder="Patel"
                    className={AUTH_STYLES.input}
                  />
                </div>
              </div>
            )}
            <div className={AUTH_STYLES.field}>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                placeholder="you@example.com"
                className={AUTH_STYLES.input}
              />
            </div>
            {mode !== "forgot" && (
              <div className={AUTH_STYLES.field}>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete={mode === "signin" ? "current-password" : "new-password"}
                  required
                  minLength={mode === "signup" ? 8 : undefined}
                  value={password}
                  onChange={(ev) => setPassword(ev.target.value)}
                  placeholder="••••••••"
                  className={AUTH_STYLES.input}
                />
              </div>
            )}
            {mode === "signin" && (
              <div className="flex justify-end -mt-2">
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                  onClick={() => { setMode("forgot"); setMessage(null); }}
                >
                  Forgot password?
                </button>
              </div>
            )}
            <Button type="submit" className={AUTH_STYLES.primary} disabled={submitting || (mode === "forgot" && resendCooldown > 0)}>
              {submitting
                ? "Please wait…"
                : mode === "forgot"
                  ? resendCooldown > 0
                    ? `Resend link in ${resendCooldown}s`
                    : message?.type === "ok"
                      ? "Resend link"
                      : "Send reset link"
                  : mode === "signin"
                    ? "Sign in"
                    : "Create account"}
            </Button>
          </form>

          {mode === "forgot" && forgotOutcome === "not_found" && (
            <Button
              type="button"
              variant="outline"
              className="h-[42px] w-full rounded-md text-sm"
              onClick={() => {
                setMode("signup");
                setMessage(null);
                setForgotOutcome(null);
              }}
            >
              Create an account
            </Button>
          )}
          {mode === "forgot" && forgotOutcome === "no_password" && (
            <button
              type="button"
              onClick={signInWithGoogle}
              disabled={submitting}
              className={AUTH_STYLES.secondary}
            >
              <GoogleIcon />
              Continue with Google
            </button>
          )}

          {mode !== "forgot" && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-foreground/10" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-muted-foreground">or</span>
                </div>
              </div>
              <button
                type="button"
                onClick={signInWithGoogle}
                disabled={submitting}
                className={AUTH_STYLES.secondary}
              >
                <GoogleIcon />
                Continue with Google
              </button>
            </>
          )}
        </CardContent>
        <CardFooter className="flex flex-col gap-3">
          {mode === "forgot" ? (
            <button
              type="button"
              className={AUTH_STYLES.footerLink}
              onClick={() => { setMode("signin"); setMessage(null); setForgotOutcome(null); }}
            >
              Back to sign in
            </button>
          ) : (
            <>
              <button
                type="button"
                className={AUTH_STYLES.footerLink}
                onClick={() => {
                  setMode(mode === "signin" ? "signup" : "signin");
                  setMessage(null);
                }}
              >
                {mode === "signin" ? "Need an account? Sign up" : "Already have an account? Sign in"}
              </button>
            </>
          )}
        </CardFooter>
      </Card>
    </AuthShell>
  );
}
